import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useRedeem } from "../useRedeem";
import { useSettings } from "../useSettings";
import { usePix } from "../usePix";
import { format, subMonths } from "date-fns";
import { formatDateText } from "@/utils";

interface TreasuryProps {
  children: ReactNode;
}
type RefreshOption = "balance" | "ledger";

interface TreasuryContextData {
  getBalanceInitialData: () => Promise<void>;
  refreshTreasuryData: (options: RefreshOption[]) => Promise<void>;
  debitAccountAmount: number;
  redeemAccountAmount: number;
  provisionedAmount: number;
  futurePostings: number;
  totalAmount: number;
  ledger: any[];
  debitPixKeys: any[];
  isLoadingRedeem: boolean;
  isLoadingBalance: boolean;
  isLoadingLedger: boolean;
  activeTab: string;
  setActiveTab: any;
  filterState: any;
  setFilterState: any;
}

const TreasuryContext = createContext<TreasuryContextData>(
  {} as TreasuryContextData,
);

const TreasuryProvider = ({ children }: TreasuryProps) => {
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const {
    getBalance,
    getReedemAmount,
    getLedger,
    getFuturePostings,
  } = useRedeem();
  const { getBankingAccount } = useSettings();
  const { getPixKeys } = usePix();
  const [debitAccountAmount, setDebitAccountAmount] = useState(0);
  const [redeemAccountAmount, setRedeemAccountAmount] = useState(0);
  const [provisionedAmount, setProvisionedAmount] = useState(0);
  const [futurePostings, setFuturePostings] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [debitPixKeys, setDebitPixKeys] = useState([]);
  const [isLoadingRedeem, setIsLoadingRedeem] = useState(false);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [activeTab, setActiveTab] = useState("CHECKING_ACCOUNT");


const [filterState, setFilterState] = useState({
    startDate: format(subMonths(new Date(), 1), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
  });
  const totalAmount = useMemo(() => {
    return debitAccountAmount + redeemAccountAmount;
  }, [debitAccountAmount, redeemAccountAmount]);

  function formatLedgerApiData(ledgerItem) {
    return {
      data: formatDateText(ledgerItem.execution_date),
      operacao: ledgerItem.description,
      valor: ledgerItem.amount_display,
      positivo: ledgerItem.positivo,
    };
  }
  async function getBalanceInitialData() {
    try {
      setIsLoadingRedeem(true);
      setIsLoadingBalance(true);
      setIsLoadingLedger(true);
      const promises = [
        getReedemAmount(),
        getBalance(),
        getLedger(activeTab, filterState.startDate, filterState.endDate),
        getBankingAccount(),
      ];
      const [redeemRes, balanceRes, ledgerRes, bankingAccountRes] =
        await Promise.allSettled(promises);
      if (redeemRes.status === "fulfilled") {
        const data = redeemRes.value?.data;
        setRedeemAccountAmount(data?.amount ?? 0);
        setProvisionedAmount(data?.provisionedAmount ?? 0);
      }
      setIsLoadingRedeem(false);
      if (balanceRes.status === "fulfilled") {
        const data = balanceRes.value?.data;
        setDebitAccountAmount(data?.debitAccountAmount ?? 0);
      }
      setIsLoadingBalance(false);
      if (ledgerRes.status === "fulfilled") {
        const data = ledgerRes.value?.data;
        const formattedData = data?.data?.map(formatLedgerApiData);
        setLedger(formattedData);
      }
      setIsLoadingLedger(false);
      if (
        user?.roles?.includes("B_SELLER_ADMIN") ||
        user?.roles?.includes("BANKING_ADMIN") ||
        user?.roles?.includes("BANKING_WALLET")
      ) {
        if (bankingAccountRes.status === "fulfilled") {
          try {
            const pixKeysRes = await getPixKeys();
            const allKeys = pixKeysRes?.data ?? [];
            setDebitPixKeys(
              allKeys.filter((item: any) => item?.accountType !== "INVESTMENT_ACCOUNT")
            );
          } catch {}
        }
      }
    } catch (e) {
      setIsLoadingRedeem(false);
      setIsLoadingBalance(false);
      setIsLoadingLedger(false);
    }
  }

  async function refreshTreasuryData(options: RefreshOption[] = []) {
    try {
      const promises: Promise<any>[] = [];
      const keys: RefreshOption[] = [];

      if (options.includes("balance")) {
        promises.push(getBalance());
        keys.push("balance");
      }
      if (options.includes("ledger")) {
        promises.push(
          getLedger(activeTab, filterState?.startDate, filterState?.endDate),
        );
        keys.push("ledger");
        setIsLoadingLedger(true);
      }

      const results = await Promise.allSettled(promises);

      results.forEach((result, index) => {
        const key = keys[index];

        if (result.status === "fulfilled") {
          const data = result.value?.data;
          switch (key) {
            case "balance":
              setDebitAccountAmount(data?.debitAccountAmount ?? 0);
              break;
            case "ledger":
              const formattedData = data?.data?.map(formatLedgerApiData);
              setIsLoadingLedger(false);
              setLedger(formattedData);
              break;
          }
        }
      });
    } catch (e) {
    }
  }


  return (
    <TreasuryContext.Provider
      value={{
        getBalanceInitialData,
        refreshTreasuryData,
        debitAccountAmount,
        redeemAccountAmount,
        provisionedAmount,
        futurePostings,
        debitPixKeys,
        totalAmount,
        ledger,
        isLoadingRedeem,
        isLoadingBalance,
        isLoadingLedger,
        activeTab,
        setActiveTab,
        filterState,
        setFilterState,
      }}
    >
      {children}
    </TreasuryContext.Provider>
  );
};

export function useTreasury(): TreasuryContextData {
  return useContext(TreasuryContext);
}

export { TreasuryProvider };
